import { useState, useRef } from 'react'
import Modal from '@/components/layout/Modal'
import { showToast } from '@/components/layout/Toast'
import { useSupabase } from '@/hooks/useSupabase'
import { downloadExcelTemplate } from '@/lib/utils'
import type { EstadoTarea, PrioridadTarea, TipoCobro, TipoTarea } from '@/lib/types'
import {
  ESTADOS_TAREA,
  PRIORIDADES_TAREA,
  TIPOS_COBRO,
  TIPOS_TAREA,
} from '@/lib/types'

/* ─── Template columns (what users must fill) ─────────────── */
const TEMPLATE_HEADERS = [
  'titulo',
  'estado',
  'prioridad',
  'tipo',
  'horas_estimadas',
  'tipo_cobro',
  'monto',
  'fecha_limite',
  'notas',
]

/* ─── Row shape after parsing ─────────────────────────────── */
interface ParsedRow {
  titulo: string
  estado: EstadoTarea
  prioridad: PrioridadTarea
  tipo: TipoTarea
  horas_estimadas: number
  tipo_cobro: TipoCobro
  monto: number
  fecha_limite: string
  notas: string
  // validation
  _errors: string[]
  _valid: boolean
}

/* ─── Validators ──────────────────────────────────────────── */
function parseRow(raw: Record<string, unknown>, idx: number): ParsedRow {
  const errors: string[] = []

  const titulo = String(raw['titulo'] ?? '').trim()
  if (!titulo) errors.push('Título requerido')

  const estadoRaw = String(raw['estado'] ?? 'pendiente').trim().toLowerCase()
  const estado = ESTADOS_TAREA.includes(estadoRaw as EstadoTarea)
    ? (estadoRaw as EstadoTarea)
    : 'pendiente'
  if (raw['estado'] && !ESTADOS_TAREA.includes(estadoRaw as EstadoTarea))
    errors.push(`Estado inválido: "${raw['estado']}" (usa: ${ESTADOS_TAREA.join(', ')})`)

  const prioridadRaw = String(raw['prioridad'] ?? 'media').trim().toLowerCase()
  const prioridad = PRIORIDADES_TAREA.includes(prioridadRaw as PrioridadTarea)
    ? (prioridadRaw as PrioridadTarea)
    : 'media'

  const tipoRaw = String(raw['tipo'] ?? 'tarea').trim().toLowerCase()
  const tipo = TIPOS_TAREA.includes(tipoRaw as TipoTarea)
    ? (tipoRaw as TipoTarea)
    : 'tarea'

  const horas_estimadas = parseFloat(String(raw['horas_estimadas'] ?? '0')) || 0

  const tipoCobroRaw = String(raw['tipo_cobro'] ?? 'sin_costo').trim().toLowerCase()
  const tipo_cobro = TIPOS_COBRO.includes(tipoCobroRaw as TipoCobro)
    ? (tipoCobroRaw as TipoCobro)
    : 'sin_costo'

  const monto = parseFloat(String(raw['monto'] ?? '0')) || 0

  const fechaRaw = String(raw['fecha_limite'] ?? '').trim()
  const fecha_limite = fechaRaw && /^\d{4}-\d{2}-\d{2}$/.test(fechaRaw) ? fechaRaw : ''
  if (fechaRaw && !fecha_limite)
    errors.push(`Fecha inválida: "${fechaRaw}" (formato: YYYY-MM-DD)`)

  const notas = String(raw['notas'] ?? '').trim()

  void idx // used externally for display

  return {
    titulo,
    estado,
    prioridad,
    tipo,
    horas_estimadas,
    tipo_cobro,
    monto,
    fecha_limite,
    notas,
    _errors: errors,
    _valid: errors.length === 0,
  }
}

/* ═══════════════════════════════════════════════════════════
   Props
═══════════════════════════════════════════════════════════ */
interface ImportarTareasModalProps {
  proyectoId: number
  proyectoNombre: string
  onClose: () => void
  onImported: () => void
}

/* ═══════════════════════════════════════════════════════════
   Component
═══════════════════════════════════════════════════════════ */
export default function ImportarTareasModal({
  proyectoId,
  proyectoNombre,
  onClose,
  onImported,
}: ImportarTareasModalProps) {
  const db = useSupabase()
  const fileRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [importing, setImporting] = useState(false)

  const validRows = rows.filter(r => r._valid)
  const invalidRows = rows.filter(r => !r._valid)

  /* ─── Parse uploaded file ──────────────────────────────── */
  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)

    try {
      const XLSX = await import('xlsx')
      const buffer = await file.arrayBuffer()
      const wb = XLSX.read(buffer, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' })

      if (raw.length === 0) {
        showToast('El archivo está vacío o no tiene filas válidas', 'error')
        return
      }

      const parsed = raw.map((r, i) => parseRow(r, i))
      setRows(parsed)
      setStep(2)
    } catch {
      showToast('Error al leer el archivo. Verifica que sea un .xlsx válido', 'error')
    }
  }

  /* ─── Batch insert ─────────────────────────────────────── */
  async function handleImport() {
    if (validRows.length === 0) return
    setImporting(true)
    try {
      let lastOrden = Date.now() // use timestamp as base order to avoid conflicts
      for (const row of validRows) {
        await db.add('tareas', {
          proyecto_id: proyectoId,
          titulo: row.titulo,
          estado: row.estado,
          prioridad: row.prioridad,
          tipo: row.tipo,
          horas_estimadas: row.horas_estimadas,
          horas_reales: 0,
          tipo_cobro: row.tipo_cobro,
          monto: row.monto,
          fecha_limite: row.fecha_limite || null,
          notas: row.notas || null,
          estado_aprobacion: 'pendiente',
          etiquetas: [],
          orden: lastOrden++,
        })
      }
      setStep(3)
      showToast(`${validRows.length} tarea${validRows.length !== 1 ? 's' : ''} importadas ✓`, 'success')
      onImported()
    } catch (e) {
      showToast('Error al importar: ' + (e instanceof Error ? e.message : 'desconocido'), 'error')
    } finally {
      setImporting(false)
    }
  }

  /* ─── Download template ────────────────────────────────── */
  async function handleTemplate() {
    await downloadExcelTemplate(TEMPLATE_HEADERS, 'Plantilla-Tareas-1Conecta')
    showToast('Plantilla descargada ✓', 'success')
  }

  return (
    <Modal title="Importar Tareas desde Excel" isOpen onClose={onClose} closeOnOverlay>
      {/* ── Step indicator ── */}
      <div className="import-steps">
        {(['Subir archivo', 'Revisar filas', 'Completado'] as const).map((label, i) => (
          <div key={i} className={`import-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
            <div className="import-step-num">{step > i + 1 ? '✓' : i + 1}</div>
            <span>{label}</span>
          </div>
        ))}
      </div>

      {/* ══ STEP 1: Upload ══════════════════════════════════ */}
      {step === 1 && (
        <div className="import-upload-area">
          <div className="import-info-box">
            <p>
              Sube un archivo <strong>.xlsx</strong> con las tareas del proyecto{' '}
              <strong>{proyectoNombre}</strong>.
            </p>
            <p style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Columnas requeridas: <code>titulo</code><br />
              Columnas opcionales: <code>estado</code>, <code>prioridad</code>, <code>tipo</code>,{' '}
              <code>horas_estimadas</code>, <code>tipo_cobro</code>, <code>monto</code>,{' '}
              <code>fecha_limite</code> (YYYY-MM-DD), <code>notas</code>
            </p>
          </div>

          <div
            className="import-drop-zone"
            onClick={() => fileRef.current?.click()}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <p>Haz click para seleccionar un archivo <strong>.xlsx</strong></p>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>o arrastra el archivo aquí</p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFile}
            />
          </div>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button
              className="btn btn-secondary"
              onClick={handleTemplate}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Descargar plantilla .xlsx
            </button>
          </div>
        </div>
      )}

      {/* ══ STEP 2: Preview / Validate ═══════════════════════ */}
      {step === 2 && (
        <div className="import-preview">
          {/* Summary bar */}
          <div className="import-summary">
            <span className="import-summary-file">📄 {fileName}</span>
            <span className="import-badge valid">{validRows.length} válidas</span>
            {invalidRows.length > 0 && (
              <span className="import-badge invalid">{invalidRows.length} con errores</span>
            )}
          </div>

          {/* Table preview */}
          <div className="import-table-wrap">
            <table className="import-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Título</th>
                  <th>Estado</th>
                  <th>Prioridad</th>
                  <th>Tipo</th>
                  <th>Hrs</th>
                  <th>Cobro</th>
                  <th>Monto</th>
                  <th>F. Límite</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className={row._valid ? 'row-valid' : 'row-invalid'}>
                    <td>{i + 1}</td>
                    <td>
                      <strong>{row.titulo || <em style={{ color: 'var(--danger)' }}>vacío</em>}</strong>
                      {row._errors.length > 0 && (
                        <div className="import-row-errors">
                          {row._errors.map((e, j) => <span key={j}>⚠ {e}</span>)}
                        </div>
                      )}
                    </td>
                    <td><span className={`badge badge-${row.estado}`}>{row.estado}</span></td>
                    <td>{row.prioridad}</td>
                    <td>{row.tipo}</td>
                    <td>{row.horas_estimadas || '—'}</td>
                    <td style={{ fontSize: '0.75rem' }}>{row.tipo_cobro.replace('_', ' ')}</td>
                    <td>{row.monto > 0 ? `$${row.monto}` : '—'}</td>
                    <td style={{ fontSize: '0.8rem' }}>{row.fecha_limite || '—'}</td>
                    <td>
                      {row._valid
                        ? <span style={{ color: 'var(--success)' }}>✓</span>
                        : <span style={{ color: 'var(--danger)' }}>✗</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => { setStep(1); setRows([]); setFileName('') }}>
              ← Cambiar archivo
            </button>
            {validRows.length > 0 && (
              <button
                className="btn btn-primary"
                onClick={handleImport}
                disabled={importing}
              >
                {importing
                  ? 'Importando...'
                  : `Importar ${validRows.length} tarea${validRows.length !== 1 ? 's' : ''}`
                }
              </button>
            )}
          </div>
        </div>
      )}

      {/* ══ STEP 3: Success ══════════════════════════════════ */}
      {step === 3 && (
        <div className="import-success">
          <div className="import-success-icon">✓</div>
          <h3>{validRows.length} tareas importadas exitosamente</h3>
          <p style={{ color: 'var(--text-secondary)', margin: '8px 0 24px' }}>
            Las tareas ya aparecen en la vista lista y Kanban del proyecto.
          </p>
          {invalidRows.length > 0 && (
            <p style={{ color: 'var(--warning)', fontSize: '0.85rem', marginBottom: 16 }}>
              ⚠ {invalidRows.length} fila{invalidRows.length !== 1 ? 's' : ''} con errores no fueron importadas.
            </p>
          )}
          <button className="btn btn-primary" onClick={onClose}>
            Volver al proyecto
          </button>
        </div>
      )}
    </Modal>
  )
}
